import React from 'react';
import Section from './Section';
import ContextStepPanel from '../wizard/context/ContextStepPanel';
import { BomAnswer, ContextQuestion, ExactPart } from '../wizard/context/useContextStep';

/**
 * /design — Wizard Step 3a context-question states. Compare with
 * `design/RFQ Wizard v2 Step 3a.dc.html`; the per-component blocks and the
 * collapsed "Robbie already filled N specs" line at the foot of each extend it. Static sample data only — the
 * connected step feeds real analysis output instead. The last section is a
 * run analysed before per-row questions (one flat list).
 */

const QUESTIONS: ContextQuestion[] = [
  {
    field: 'robotCategory',
    question: 'Is this for an AMR / warehouse robot?',
    answerType: 'single_select',
    options: ['AMR / Warehouse Robot', 'AGV', 'Mobile manipulator'],
    isRobotCategoryConfirmation: true,
    detectedValue: 'AMR / Warehouse Robot',
  },
  {
    field: 'd_safety_standard',
    question: 'What is the Standard?',
    componentTypes: ['Servomotor'],
    helpText: 'Narrows suppliers to the ones already certified for your line.',
    answerType: 'multi_select',
    options: ['IEC 61800-5-2', 'ISO 13849-1', 'EN 60204-1', 'IEC 61508'],
  },
  {
    field: 'payloadKg',
    question: 'What payload will the robot carry?',
    helpText: 'Determines the torque and bearing ratings suppliers must meet.',
    unit: 'kg',
    placeholder: 'e.g. 250',
  },
  {
    field: 'environment',
    question: 'Operating environment?',
    helpText: 'Affects IP rating and material requirements.',
    answerType: 'single_select',
    options: ['Indoor warehouse', 'Cold storage', 'Outdoor / mixed', 'Cleanroom'],
  },
  {
    field: 'dutyCycle',
    question: 'Expected duty cycle?',
    helpText: 'Hours per day the drive system runs — sets the reliability bar.',
    unit: 'h/day',
    placeholder: 'e.g. 16',
  },
];

/** The October 2026 feedback BOM as the per-row analysis returns it. */
const FC = { componentIndex: 0, componentKey: 'c-fc', componentName: 'Flight controller — quadcopter, 4 motors' };
const LIPO = { componentIndex: 1, componentKey: 'c-lipo', componentName: 'LiPo battery — 3S 11.1 V 5200 mAh' };
const MOTOR = { componentIndex: 3, componentKey: 'c-motor', componentName: 'Brushless motor — for a 5 inch quad' };

const PER_COMPONENT_QUESTIONS: ContextQuestion[] = [
  {
    field: 'robotCategory',
    question: 'We detected this BOM is for a Drone. Is that correct?',
    answerType: 'single_select',
    options: ['Drone', 'Mobile Robot', 'Articulated Robot'],
    isRobotCategoryConfirmation: true,
    detectedValue: 'Drone',
  },
  {
    ...FC,
    field: 'c-fc_firmware',
    specId: 'firmware',
    question: 'Which flight firmware should the controller run?',
    helpText: 'Boards are sold per firmware target.',
    answerType: 'single_select',
    options: ['ArduPilot', 'PX4', 'Betaflight', 'INAV'],
  },
  {
    ...FC,
    field: 'c-fc_mounting_pattern',
    specId: 'mounting_pattern',
    question: 'What mounting pattern does the frame take?',
    helpText: 'A board that does not fit the stack is the wrong board.',
    answerType: 'single_select',
    options: ['30.5×30.5 mm', '20×20 mm', '16×16 mm'],
  },
  {
    ...LIPO,
    field: 'c-lipo_discharge_rate',
    specId: 'discharge_rate',
    question: 'What continuous discharge rating should the 3S 5200 mAh pack have?',
    helpText: 'Too low a C rating sags under full throttle.',
    answerType: 'single_select',
    options: ['25C', '35C', '50C'],
  },
  {
    ...MOTOR,
    field: 'c-motor_kv',
    specId: 'kv',
    question: 'What Kv rating should the motors have?',
    helpText: 'Kv and cell count together set prop speed.',
    unit: 'Kv',
    placeholder: 'e.g. 1750',
  },
];

const BOM_ANSWERS: BomAnswer[] = [
  { ...FC, field: 'c-fc_motor_outputs', label: 'Motor outputs', value: '4', evidence: '4 motors', source: 'bom_text' },
  { ...LIPO, field: 'c-lipo_cell_count', label: 'Cell count', value: '3S', evidence: '3S', source: 'bom_text' },
  { ...LIPO, field: 'c-lipo_nominal_voltage', label: 'Nominal voltage', value: '11.1 V', evidence: '11.1 V', source: 'bom_text' },
  { ...LIPO, field: 'c-lipo_capacity', label: 'Capacity', value: '5200 mAh', evidence: '5200 mAh', source: 'bom_text' },
];

const EXACT_PARTS: ExactPart[] = [
  {
    componentIndex: 2,
    componentKey: 'c-plc',
    componentName: 'Siemens SIMATIC S7-1200 CPU 1214C',
    partNumber: '6ES7214-1AG40-0XB0',
    source: 'bom_text',
  },
];

const resolve = () => Promise.resolve();
const noop = () => undefined;

const ContextStepGallery: React.FC = () => (
  <>
    <Section title="Wizard step 3a — questions per component, what Robbie read from the BOM collapsed at the foot of each">
      <div className="mx-auto max-w-content">
        <ContextStepPanel
          questions={PER_COMPONENT_QUESTIONS}
          bomAnswers={BOM_ANSWERS}
          exactParts={EXACT_PARTS}
          isAnalyzing={false}
          progress={{ completed: 4, total: 4 }}
          onSubmit={resolve}
          onBack={noop}
          onContinue={noop}
        />
      </div>
    </Section>
    <Section title="Wizard step 3a — nothing left to ask: everything was in the BOM">
      <div className="mx-auto max-w-content">
        <ContextStepPanel
          questions={[]}
          bomAnswers={BOM_ANSWERS}
          exactParts={EXACT_PARTS}
          isAnalyzing={false}
          progress={{ completed: 4, total: 4 }}
          onSubmit={resolve}
          onBack={noop}
          onContinue={noop}
        />
      </div>
    </Section>
    <Section title="Wizard step 3a — context questions (detected value pre-selected)">
      <div className="mx-auto max-w-content">
        <ContextStepPanel
          questions={QUESTIONS}
          isAnalyzing={false}
          progress={{ completed: 34, total: 34 }}
          onSubmit={resolve}
          onBack={noop}
          onContinue={noop}
        />
      </div>
    </Section>
    <Section title="Wizard step 3a — analyzing · context complete">
      <div className="mx-auto flex max-w-content flex-col gap-s7">
        <ContextStepPanel
          questions={[]}
          isAnalyzing
          progress={{ completed: 0, total: 34 }}
          onSubmit={resolve}
          onBack={noop}
          onContinue={noop}
        />
        <ContextStepPanel
          questions={[]}
          isAnalyzing={false}
          progress={{ completed: 34, total: 34 }}
          onSubmit={resolve}
          onBack={noop}
          onContinue={noop}
        />
      </div>
    </Section>
  </>
);

export default ContextStepGallery;
